// src/RichTextEditor.js
// Простой редактор текста поста без внешних зависимостей.
// Работает на contentEditable: панель кнопок применяет форматирование
// к выделению, наружу отдаётся готовый HTML.

import React, { useCallback, useEffect, useRef } from 'react';
import sanitizeHtml from './sanitizeHtml';
import './RichTextEditor.css';

const HIGHLIGHT = '#fff3a3';

const TOOLBAR = [
  {
    group: [
      { id: 'bold', title: 'Полужирный (Ctrl+B)', label: 'Ж',
        style: { fontWeight: 800 } },
      { id: 'italic', title: 'Курсив (Ctrl+I)', label: 'К',
        style: { fontStyle: 'italic' } },
      { id: 'underline', title: 'Подчёркнутый (Ctrl+U)', label: 'Ч',
        style: { textDecoration: 'underline' } },
      { id: 'strikeThrough', title: 'Зачёркнутый', label: 'З',
        style: { textDecoration: 'line-through' } },
    ],
  },
  {
    group: [
      { id: 'highlight', title: 'Выделить маркером', label: 'А',
        style: { background: HIGHLIGHT, borderRadius: '3px' } },
    ],
  },
  {
    group: [
      { id: 'insertUnorderedList', title: 'Маркированный список', icon: 'ul' },
      { id: 'insertOrderedList', title: 'Нумерованный список', icon: 'ol' },
    ],
  },
  {
    group: [
      { id: 'createLink', title: 'Вставить ссылку', icon: 'link' },
      { id: 'removeFormat', title: 'Убрать форматирование', icon: 'clear' },
    ],
  },
];

const icons = {
  ul: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="9" y1="6" x2="20" y2="6"></line>
      <line x1="9" y1="12" x2="20" y2="12"></line>
      <line x1="9" y1="18" x2="20" y2="18"></line>
      <circle cx="4.5" cy="6" r="1.5" fill="currentColor" stroke="none"></circle>
      <circle cx="4.5" cy="12" r="1.5" fill="currentColor" stroke="none"></circle>
      <circle cx="4.5" cy="18" r="1.5" fill="currentColor" stroke="none"></circle>
    </svg>
  ),
  ol: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="10" y1="6" x2="20" y2="6"></line>
      <line x1="10" y1="12" x2="20" y2="12"></line>
      <line x1="10" y1="18" x2="20" y2="18"></line>
      <text x="2" y="8" fontSize="7" fill="currentColor" stroke="none">1</text>
      <text x="2" y="14.5" fontSize="7" fill="currentColor" stroke="none">2</text>
      <text x="2" y="21" fontSize="7" fill="currentColor" stroke="none">3</text>
    </svg>
  ),
  link: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
    </svg>
  ),
  clear: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 7V4h16v3"></path>
      <path d="M9 20h6"></path>
      <path d="M12 4v16"></path>
      <line x1="4" y1="20" x2="20" y2="4" stroke="#c53030"></line>
    </svg>
  ),
};

export default function RichTextEditor({ value, onChange, placeholder, id }) {
  const ref = useRef(null);
  // Пока пользователь печатает, innerHTML заново не выставляем:
  // иначе каретка каждый раз прыгает в начало поля.
  const lastEmitted = useRef('');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = value || '';
    if (next !== lastEmitted.current && next !== el.innerHTML) {
      el.innerHTML = sanitizeHtml(next);
      lastEmitted.current = next;
    }
  }, [value]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const html = el.innerHTML;
    lastEmitted.current = html;
    onChange(html);
  }, [onChange]);

  const exec = (command, argument = null) => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    document.execCommand(command, false, argument);
    emit();
  };

  // execCommand красит выделение инлайновым стилем на <span>. Приводим это
  // к тегу <mark>: он переживает очистку HTML и перекрашивается вместе с темой,
  // тогда как жёстко записанный жёлтый фон в тёмной теме нечитаем.
  const normalizeHighlights = (root) => {
    root.querySelectorAll('span[style]').forEach((span) => {
      const bg = span.style.backgroundColor;
      if (!bg) return;
      const highlighted = /255,\s*243,\s*163/.test(bg);
      span.style.removeProperty('background-color');
      if (!span.getAttribute('style')) span.removeAttribute('style');
      if (!highlighted) return;

      const mark = document.createElement('mark');
      span.replaceWith(mark);
      mark.appendChild(span);
      // Если на span не осталось ни стилей, ни классов — обёртка не нужна
      if (span.attributes.length === 0) {
        span.replaceWith(...Array.from(span.childNodes));
      }
    });
    // Снятое выделение оставляет пустые <mark> — убираем их
    root.querySelectorAll('mark').forEach((mark) => {
      if (!mark.textContent) mark.remove();
    });
  };

  const unwrapMarks = (root) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;
    const range = selection.getRangeAt(0);
    const marks = Array.from(root.querySelectorAll('mark'))
      .filter((m) => range.intersectsNode(m));
    if (marks.length === 0) return false;
    marks.forEach((m) => m.replaceWith(...Array.from(m.childNodes)));
    return true;
  };

  const handleCommand = (id) => {
    if (id === 'highlight') {
      const el = ref.current;
      el.focus();
      // Повторное нажатие на уже выделенном тексте снимает выделение
      if (unwrapMarks(el)) {
        emit();
        return;
      }
      const ok = document.execCommand('hiliteColor', false, HIGHLIGHT);
      if (!ok) document.execCommand('backColor', false, HIGHLIGHT);
      normalizeHighlights(el);
      emit();
      return;
    }
    if (id === 'createLink') {
      const selection = window.getSelection();
      const selected = selection ? selection.toString() : '';
      if (!selected) {
        window.alert('Сначала выделите текст, который станет ссылкой.');
        return;
      }
      // eslint-disable-next-line no-alert
      const url = window.prompt('Адрес ссылки:', 'https://');
      if (!url) return;
      exec('createLink', url);
      return;
    }
    exec(id);
  };

  const handleKeyDown = (e) => {
    const meta = e.ctrlKey || e.metaKey;
    if (!meta) return;
    const key = e.key.toLowerCase();
    if (key === 'b') { e.preventDefault(); exec('bold'); }
    else if (key === 'i') { e.preventDefault(); exec('italic'); }
    else if (key === 'u') { e.preventDefault(); exec('underline'); }
  };

  // Вставка из Word и с сайтов тянет за собой чужую разметку — чистим её
  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (html) {
      document.execCommand('insertHTML', false, sanitizeHtml(html));
    } else {
      document.execCommand('insertText', false, text);
    }
    emit();
  };

  const isEmpty = !value || value === '<br>' || value.replace(/<[^>]*>/g, '').trim() === '';

  return (
    <div className="rte">
      <div className="rte-toolbar" role="toolbar" aria-label="Форматирование текста">
        {TOOLBAR.map((section, i) => (
          <div className="rte-group" key={i}>
            {section.group.map((btn) => (
              <button
                key={btn.id}
                type="button"
                className="rte-button"
                title={btn.title}
                aria-label={btn.title}
                // Нажатие мышью не должно снимать выделение в тексте
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleCommand(btn.id)}
              >
                {btn.icon ? icons[btn.icon] : <span style={btn.style}>{btn.label}</span>}
              </button>
            ))}
          </div>
        ))}
      </div>

      <div
        id={id}
        ref={ref}
        className={`rte-content ${isEmpty ? 'is-empty' : ''}`}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Содержание поста"
        data-placeholder={placeholder || 'Текст публикации...'}
        onInput={emit}
        onBlur={emit}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
      />
    </div>
  );
}
